import { ID } from './ID';
import { CustomColumnItem } from './CustomColumnItem';
import { CustomColumnHeader, CustomColumnHeaderToClone } from './CustomColumnHeader';
import { QualQuestion, QualQuestionAppliedState } from './QualQuestion';
import { SupplierOrganization } from './SupplierOrganization';
import { SupplierQualAnswer } from './SupplierQualAnswer';
import { BidRequestExchangeRate } from './event-request';

export type ErrorState<Key extends ID = number> = Record<Key, number[]>;
export type WarningState = Record<number, number[]>;

export interface BaselineState {
  baselineCount: number;
  baselinesApplied: boolean;
  fetchBaselines: boolean;
  fetchBaselinesAfterSave: boolean;
  inProgress: boolean;
  showBaselinePriceUpdateAlert: boolean;
  zeroPricedLineItemsCount: number;
}

export interface Event {
  currency?: string;
}

// In-memory per-row NSB selection. Each field holds either a smart-baseline flavor id (e.g. 'F3') or
// 'manual' when the buyer typed the price. Absent keys mean the row follows the event-level default.
// Mapped to/from the flat line-item columns baseline_price_source / negotiation_price_source at the
// API boundary, so a row's override survives reload (no re-seed clobber).
export interface NsbRowConfig {
  baseline?: string;
  negotiation?: string;
}

export interface LineItem {
  _destroy?: boolean;
  baseline_price_source?: string | null;
  currency?: string;
  currency_code?: string;
  custom_column_items?: CustomColumnItem[];
  // Buyer's manual override for the Suggested Price cell. Denormalized from the row's anchor
  // price records (identical across per-org records by design). Presence indicates an override.
  custom_unit_price?: string | null;
  discount?: number;
  errors?: string[];
  extended_quantity: number | string;
  id: ID;
  // Write-once (NSB #3/#7): true when the buyer uploaded a price-per-unit (> 0). Set at import, never
  // changed after (DB excludes it from the update-columns list). NSB skips baselining these rows.
  is_buyer_provided_baseline_price?: boolean | null;
  is_required?: boolean;
  lot_id: ID;
  name: string;
  negotiation_price_source?: string | null;
  negotiation_starting_price?: number | string | null;
  negotiation_starting_source?: string;
  noBid?: boolean;
  original_price?: string;
  parent_line_item_id?: ID | null;
  // AI-generated anchor price for the row, denormalized off the per-org anchor price records.
  predictive_unit_price?: string | null;
  price: number | string;
  qual_questions?: QualQuestion[];
  quantity: number | string;
  reporting_source?: string;
  resolved_line_item_name?: string | null;
  sno: number;
  suggested_price?: string;
  suggested_price_desc?: string;
  suggested_price_override?: number | string | null;
  supplier_qual_answers?: SupplierQualAnswer[];
  surcharge?: number;
  system_modified?: boolean;
  /** Whether this tier counts toward totals for its group; null for line items outside a tier group. */
  tier_counted?: boolean | null;
  tier_position?: number | null;
  units?: string;
  updated_at?: string;
}

export interface Lot {
  _collapsed?: boolean;
  _destroy?: boolean;
  adder_column_limit?: number;
  created_at: string;
  custom_column_headers: CustomColumnHeader[];
  description?: string | null;
  errors?: string[];
  has_baseline: boolean;
  has_required_line_items?: boolean;
  has_tier_line_items?: boolean;
  id: ID;
  incumbent_id?: ID;
  line_item_questions_count: number;
  line_items: LineItem[];
  line_items_count: number;
  name: string;
  sno: number;
  updated_at?: string;
}

export interface FeedbackCellValue {
  percentage?: number | null;
  price?: number | null;
  pricePerUnit?: number | null;
  rank?: string;
}

export type LineItemCellValue = FeedbackCellValue | boolean | number | string;

export type LineItemCellsArray = LineItemCellValue[];

export type LineItemChanges = Omit<LineItem, 'custom_column_items' | 'qual_questions' | 'supplier_qual_answers'> & {
  custom_column_items?: Record<ID, CustomColumnItem>;
  qual_questions?: Record<ID, QualQuestion>;
  supplier_qual_answers?: Record<ID, SupplierQualAnswer>;
};

export type LotChanges = Omit<Lot, 'custom_column_headers' | 'line_items'> & {
  applied_questions_state?: Record<string, QualQuestionAppliedState>;
  custom_column_headers?: Record<ID, CustomColumnHeader>;
  deleted_qualifying_custom_column_headers?: CustomColumnHeaderToClone[];
  line_items?: Record<ID, LineItemChanges>;
};

export type LotErrorMap = Record<number, string[]>;

export interface ImportData {
  deletedQualifyingCustomColumnHeaders: CustomColumnHeaderToClone[];
  errorLotIndexes: number[];
  importedLots: Lot[];
  lotErrorMap: LotErrorMap;
  totalExistingLineItems?: number;
  totalImportedLineItems?: number;
}

export interface ImportStatus {
  disableSave: boolean;
  imported: boolean;
  inProgress: boolean;
  saveLineItemsAfterImport: boolean;
  showDescriptiveColumnsWarningModal: boolean;
  showLineItemsModal: boolean;
  showOneSchemaImportModal: boolean;
  uploadStartedFromEmptyLot: boolean;
  uploadStartedFromNewEvent: boolean;
}

export interface ImportState {
  data: ImportData;
  status: ImportStatus;
}

export interface EventState {
  bid_request_exchange_rates?: BidRequestExchangeRate[];
  collectItemsPerUnit: boolean;
  currency: string;
  eventIncumbentId?: ID | null;
  hasInvitedOrScheduledSuppliers: boolean;
  hasSuppliers: boolean;
  isReadOnly: boolean;
  originalCurrency: string;
  originalEventIncumbentId?: ID | null;
  paginationEnabled?: boolean;
  requestType?: string;
  setBaselineAverage: boolean;
  showDiscount: boolean;
  showExtendedPriceFeedback: boolean;
  showLogisticsCharge: boolean;
  showPricingColumns: boolean;
  supplierOrganizations: SupplierOrganization[];
  zeroStartingPrices?: any[];
}

export interface LotPagination {
  currentPage: number;
  loadedPage: number;
  loadedPageSize: number;
  loads: number;
  pageIds: ID[];
  pageSize: number;
  total: number;
}

export interface LineItemsState {
  baselineState: BaselineState;
  changes: Record<ID, LotChanges>;
  event: EventState;
  importState: ImportState;
  isInProgress: boolean;
  isLoading: boolean;
  lots?: Lot[];
  lotsErrors: Record<ID, ErrorState<ID>>;
  lotsPagination: Record<ID, LotPagination>;
  originalLots: Lot[];
  refreshValidation: boolean;
  shellRefreshCount: number;
  shouldPreserveNewLotsOnRefresh: boolean;
  shouldRefresh: boolean;
  shouldRerenderLineItemData: boolean;
}

// Wire value for the `mechanism` parameter sent on line-item-mutating
// endpoints (DELETE /remove_all_lots, PUT /events/:id). Mirrors the backend's
// controller params which describe `mechanism` as the origin of the update
// (e.g. 'import' for the OneSchema replace-all upload flow). Centralized so
// adding a future mechanism is a one-line change here instead of a
// scatter-shot rename.
export const LINE_ITEM_UPDATE_MECHANISM = {
  IMPORT: 'import'
} as const;

export type LineItemUpdateMechanism =
  typeof LINE_ITEM_UPDATE_MECHANISM[keyof typeof LINE_ITEM_UPDATE_MECHANISM];
